import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, UserCheck, UserX, Users as UsersIcon } from 'lucide-react'

import { api } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { useAuthStore } from '@/stores/auth'
import type { Seller, SellerListResponse } from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

/**
 * Truncate a long external identifier for the table column. Keeps the first
 * 12 chars + ellipsis so admins can spot the prefix without breaking layout.
 */
function truncate(value: string | undefined | null, max = 16): string {
  if (value === undefined || value === null || value === '') return '—'
  return value.length <= max ? value : `${value.slice(0, max)}…`
}

/**
 * `/admin/sellers` — list with active filter + search, "Novo Vendedor" CTA.
 *
 * RBAC:
 *   - All authenticated users can read.
 *   - "Novo Vendedor" is gated to admin/manager.
 */
export function SellersPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canMutate = role === 'admin' || role === 'manager'

  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [search, setSearch] = useState('')
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })

  const queryKey = useMemo(
    () => ['admin-sellers', { activeFilter, search }],
    [activeFilter, search],
  )

  const { data, isLoading, isError, error } = useQuery<SellerListResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (activeFilter === 'active') params.set('active', 'true')
      if (activeFilter === 'inactive') params.set('active', 'false')
      if (search.trim() !== '') params.set('search', search.trim())
      const qs = params.toString()
      return api<SellerListResponse>(`/admin/sellers${qs === '' ? '' : `?${qs}`}`)
    },
  })

  const sellers = data?.sellers ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Vendedores</h1>
          <p className="text-sm text-muted-foreground">
            Cadastre vendedores e vincule-os a calendários do Google Calendar e usuários do GHL.
          </p>
        </div>
        <div className="flex gap-2">
          {canMutate && (
            <Button asChild>
              <Link to="/admin/sellers/new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Novo Vendedor
              </Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar por status">
          {(['all', 'active', 'inactive'] as const).map((v) => (
            <Button
              key={v}
              type="button"
              variant={activeFilter === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(v)}
              aria-pressed={activeFilter === v}
            >
              {v === 'all' ? 'Todos' : v === 'active' ? 'Ativos' : 'Inativos'}
            </Button>
          ))}
        </div>
        <Input
          placeholder="Buscar por nome ou email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
          aria-label="Buscar vendedores"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Calendar ID</TableHead>
              <TableHead>GHL User ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Atualizado em</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-destructive">
                  Erro ao carregar vendedores: {(error as Error)?.message ?? 'desconhecido'}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && sellers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">
                  Nenhum vendedor encontrado.
                </TableCell>
              </TableRow>
            )}
            {sellers.map((s: Seller) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.phone}</TableCell>
                <TableCell>{s.email ?? '—'}</TableCell>
                <TableCell title={s.calendarId ?? ''}>{truncate(s.calendarId)}</TableCell>
                <TableCell title={s.ghlUserId ?? ''}>{truncate(s.ghlUserId)}</TableCell>
                <TableCell>
                  <Badge variant={s.active ? 'success' : 'secondary'}>
                    {s.active ? (
                      <>
                        <UserCheck className="mr-1 h-3 w-3" aria-hidden="true" /> Ativo
                      </>
                    ) : (
                      <>
                        <UserX className="mr-1 h-3 w-3" aria-hidden="true" /> Inativo
                      </>
                    )}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(s.updatedAt).toLocaleString('pt-BR')}</TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/admin/sellers/${s.id}`} aria-label={`Abrir ${s.name}`}>
                      <UsersIcon className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && <ToastDescription>{toast.description}</ToastDescription>}
        </div>
      </Toast>
    </div>
  )
}

export default SellersPage
